const MAX_PARTICLES = 100;

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
  shape: "square" | "star";
  angle: number;
  spin: number;
}

export interface ParticleSystem {
  particles: Particle[];
  pool: Particle[];
}

export function createParticleSystem(): ParticleSystem {
  return { particles: [], pool: [] };
}

function obtainParticle(system: ParticleSystem): Particle {
  const reused = system.pool.pop();
  if (reused != null) {
    return reused;
  }
  return {
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    life: 0,
    maxLife: 0,
    color: "#fff",
    size: 0,
    shape: "square",
    angle: 0,
    spin: 0
  };
}

function releaseParticle(system: ParticleSystem, particle: Particle): void {
  if (system.pool.length < MAX_PARTICLES) {
    system.pool.push(particle);
  }
}

export function emitMatchParticles(
  system: ParticleSystem,
  x: number,
  y: number,
  color: string,
  count: number
): void {
  const available = MAX_PARTICLES - system.particles.length;
  const toEmit = Math.min(count, available);
  for (let i = 0; i < toEmit; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 30 + Math.random() * 60;
    const life = 0.4 + Math.random() * 0.3;
    const particle = obtainParticle(system);
    particle.x = x;
    particle.y = y;
    particle.vx = Math.cos(angle) * speed;
    particle.vy = Math.sin(angle) * speed - 20;
    particle.life = life;
    particle.maxLife = life;
    particle.color = color;
    particle.size = 2 + Math.random() * 2;
    particle.shape = "square";
    particle.angle = 0;
    particle.spin = 0;
    system.particles.push(particle);
  }
}

export function emitRainbowStarParticles(
  system: ParticleSystem,
  x: number,
  y: number,
  count: number
): void {
  const available = MAX_PARTICLES - system.particles.length;
  const toEmit = Math.min(count, available);
  for (let i = 0; i < toEmit; i += 1) {
    const hue = Math.floor(Math.random() * 360);
    const angle = Math.random() * Math.PI * 2;
    const speed = 28 + Math.random() * 64;
    const life = 0.8 + Math.random() * 0.4;
    const particle = obtainParticle(system);
    particle.x = x;
    particle.y = y;
    particle.vx = Math.cos(angle) * speed;
    particle.vy = Math.sin(angle) * speed - 24;
    particle.life = life;
    particle.maxLife = life;
    particle.color = `hsl(${hue}, 98%, 68%)`;
    particle.size = 3 + Math.random() * 3;
    particle.shape = "star";
    particle.angle = Math.random() * Math.PI * 2;
    particle.spin = (Math.random() - 0.5) * 9;
    system.particles.push(particle);
  }
}

export function emitCelebrationParticles(
  system: ParticleSystem,
  centerX: number,
  centerY: number,
  count: number
): void {
  const colors = ["#ffd166", "#ff7a59", "#5cc4ff", "#7ee07f", "#cc78ff", "#ff4f9a"];
  const available = MAX_PARTICLES - system.particles.length;
  const toEmit = Math.min(count, available);
  for (let i = 0; i < toEmit; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 50 + Math.random() * 80;
    const life = 0.6 + Math.random() * 0.4;
    const particle = obtainParticle(system);
    particle.x = centerX;
    particle.y = centerY;
    particle.vx = Math.cos(angle) * speed;
    particle.vy = Math.sin(angle) * speed - 30;
    particle.life = life;
    particle.maxLife = life;
    particle.color = colors[Math.floor(Math.random() * colors.length)];
    particle.size = 2.5 + Math.random() * 2.5;
    particle.shape = "square";
    particle.angle = 0;
    particle.spin = 0;
    system.particles.push(particle);
  }
}

export function emitDirectionalParticles(
  system: ParticleSystem,
  x: number,
  y: number,
  colors: string[],
  count: number,
  options?: {
    directionRad?: number;
    spreadRad?: number;
    speedMin?: number;
    speedMax?: number;
    lifeMin?: number;
    lifeMax?: number;
    sizeMin?: number;
    sizeMax?: number;
    upwardBoost?: number;
    shape?: Particle["shape"];
    spinMin?: number;
    spinMax?: number;
  }
): void {
  const palette = colors.length > 0 ? colors : ["#ffffff"];
  const available = MAX_PARTICLES - system.particles.length;
  const toEmit = Math.min(count, available);
  const directionRad = options?.directionRad ?? (-Math.PI / 2);
  const spreadRad = options?.spreadRad ?? (Math.PI / 2);
  const speedMin = options?.speedMin ?? 18;
  const speedMax = options?.speedMax ?? 42;
  const lifeMin = options?.lifeMin ?? 0.38;
  const lifeMax = options?.lifeMax ?? 0.72;
  const sizeMin = options?.sizeMin ?? 1.6;
  const sizeMax = options?.sizeMax ?? 3.2;
  const upwardBoost = options?.upwardBoost ?? 14;
  const shape = options?.shape ?? "square";
  const spinMin = options?.spinMin ?? -6;
  const spinMax = options?.spinMax ?? 6;
  for (let i = 0; i < toEmit; i += 1) {
    const angle = directionRad + (Math.random() - 0.5) * spreadRad;
    const speed = speedMin + Math.random() * Math.max(0, speedMax - speedMin);
    const life = lifeMin + Math.random() * Math.max(0, lifeMax - lifeMin);
    const particle = obtainParticle(system);
    particle.x = x;
    particle.y = y;
    particle.vx = Math.cos(angle) * speed;
    particle.vy = Math.sin(angle) * speed - upwardBoost;
    particle.life = life;
    particle.maxLife = life;
    particle.color = palette[Math.floor(Math.random() * palette.length)] ?? palette[0];
    particle.size = sizeMin + Math.random() * Math.max(0, sizeMax - sizeMin);
    particle.shape = shape;
    particle.angle = shape === "star" ? Math.random() * Math.PI * 2 : 0;
    particle.spin = spinMin + Math.random() * Math.max(0, spinMax - spinMin);
    system.particles.push(particle);
  }
}

export function emitGameOverParticles(
  system: ParticleSystem,
  canvasWidth: number,
  count: number
): void {
  const colors = ["#ff3344", "#cc1122", "#ff6666", "#aa0011", "#ff4455"];
  const available = MAX_PARTICLES - system.particles.length;
  const toEmit = Math.min(count, available);
  for (let i = 0; i < toEmit; i++) {
    const x = Math.random() * canvasWidth;
    const vx = (Math.random() - 0.5) * 30;
    const vy = 40 + Math.random() * 60;
    const life = 1.5 + Math.random() * 1.5;
    const particle = obtainParticle(system);
    particle.x = x;
    particle.y = -4;
    particle.vx = vx;
    particle.vy = vy;
    particle.life = life;
    particle.maxLife = life;
    particle.color = colors[Math.floor(Math.random() * colors.length)];
    particle.size = 2 + Math.random() * 3;
    particle.shape = "square";
    particle.angle = 0;
    particle.spin = 0;
    system.particles.push(particle);
  }
}

export function updateParticles(system: ParticleSystem, dt: number): void {
  for (let i = system.particles.length - 1; i >= 0; i--) {
    const p = system.particles[i];
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.vy += 80 * dt;
    p.angle += p.spin * dt;
    p.life -= dt;
    if (p.life <= 0) {
      const lastIndex = system.particles.length - 1;
      const removed = system.particles[i];
      if (i !== lastIndex) {
        system.particles[i] = system.particles[lastIndex];
      }
      system.particles.pop();
      releaseParticle(system, removed);
    }
  }
}

export function drawParticles(
  ctx: CanvasRenderingContext2D,
  system: ParticleSystem
): void {
  for (const p of system.particles) {
    const alpha = Math.max(0, p.life / p.maxLife);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = p.color;
    if (p.shape === "star") {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      const outer = p.size / 2;
      const inner = outer * 0.45;
      ctx.beginPath();
      for (let i = 0; i < 5; i += 1) {
        const outerAngle = (i * Math.PI * 2) / 5 - Math.PI / 2;
        const innerAngle = outerAngle + Math.PI / 5;
        const ox = Math.cos(outerAngle) * outer;
        const oy = Math.sin(outerAngle) * outer;
        const ix = Math.cos(innerAngle) * inner;
        const iy = Math.sin(innerAngle) * inner;
        if (i === 0) {
          ctx.moveTo(ox, oy);
        } else {
          ctx.lineTo(ox, oy);
        }
        ctx.lineTo(ix, iy);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    } else {
      ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
  }
  ctx.globalAlpha = 1;
}
